/**
 * Currency formatting — single source of truth for all monetary rendering.
 *
 * Every caller passes the location/reporting currency in. `null`/`undefined`
 * collapses to `'USD'`, which is how legacy locations (NULL `Location.currency`)
 * render today. JPY has zero minor units; the helper applies the correct
 * fraction-digit defaults unless the caller explicitly overrides them — so a
 * caller never needs to know about per-currency display rules.
 *
 * Do NOT add new `Intl.NumberFormat('en-US', { currency: 'USD' })` literals
 * elsewhere. A pre-commit grep enforces this.
 */

export const SUPPORTED_CURRENCIES = ['USD', 'CAD', 'JPY', 'EUR', 'GBP', 'AUD', 'MXN', 'SGD'];

export const CURRENCY_LOCALE = {
  USD: 'en-US',
  CAD: 'en-CA',
  JPY: 'ja-JP',
  EUR: 'de-DE',
  GBP: 'en-GB',
  AUD: 'en-AU',
  MXN: 'es-MX',
  SGD: 'en-SG',
};

export const CURRENCY_FRACTION = {
  USD: 2,
  CAD: 2,
  JPY: 0,
  EUR: 2,
  GBP: 2,
  AUD: 2,
  MXN: 2,
  SGD: 2,
};

// SheetJS / ExcelJS `numFmt` strings keyed by currency. Single source so all
// exporters render the same way.
export const EXCEL_NUM_FMT = {
  USD: '"$"#,##0.00',
  CAD: '"C$"#,##0.00',
  JPY: '"¥"#,##0',
  EUR: '"€"#,##0.00',
  GBP: '"£"#,##0.00',
  AUD: '"A$"#,##0.00',
  MXN: '"MX$"#,##0.00',
  SGD: '"S$"#,##0.00',
};

function resolve(currency) {
  if (!currency || typeof currency !== 'string') return 'USD';
  const upper = currency.toUpperCase();
  return /^[A-Z]{3}$/.test(upper) ? upper : 'USD';
}

/**
 * Format a value as a fully-decorated currency string (symbol + locale-aware
 * grouping + correct minor-unit count for the currency).
 *
 * @param {number|null|undefined} value
 * @param {string|null|undefined} currency - ISO-4217 code. NULL → USD.
 * @param {object} [opts] - Pass-through `Intl.NumberFormat` options. The
 *   `minimumFractionDigits` / `maximumFractionDigits` defaults follow the
 *   currency (e.g. JPY → 0). Override only when you really need to.
 */
export function formatCurrency(value, currency, opts = {}) {
  const ccy = resolve(currency);
  const frac = CURRENCY_FRACTION[ccy] ?? 2;
  const safe = value == null || Number.isNaN(value) ? 0 : value;
  return new Intl.NumberFormat(CURRENCY_LOCALE[ccy] || 'en-US', {
    style: 'currency',
    currency: ccy,
    minimumFractionDigits: opts.minimumFractionDigits ?? frac,
    maximumFractionDigits: opts.maximumFractionDigits ?? frac,
    ...opts,
  }).format(safe);
}

/**
 * Rounded-to-whole-unit form. Useful for KPI tiles and chart axes that
 * don't have room for cents.
 */
export function formatCurrencyRounded(value, currency, opts = {}) {
  return formatCurrency(Math.round(value ?? 0), currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...opts,
  });
}

/**
 * Compact short form ($1.2K / $1.2M). For chart legends and dense displays.
 */
export function formatCurrencyCompact(value, currency) {
  const ccy = resolve(currency);
  const safe = value == null || Number.isNaN(value) ? 0 : value;
  return new Intl.NumberFormat(CURRENCY_LOCALE[ccy] || 'en-US', {
    style: 'currency',
    currency: ccy,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(safe);
}

/**
 * Just the symbol — for "$ / unit" axis labels or column headers where you
 * format the number separately.
 */
export function currencySymbol(currency) {
  const ccy = resolve(currency);
  const parts = new Intl.NumberFormat(CURRENCY_LOCALE[ccy] || 'en-US', {
    style: 'currency',
    currency: ccy,
  }).formatToParts(0);
  const symbol = parts.find((p) => p.type === 'currency');
  return symbol ? symbol.value : '$';
}

export function excelNumFmt(currency) {
  const ccy = resolve(currency);
  return EXCEL_NUM_FMT[ccy] || `"${ccy}" #,##0.00`;
}

export function localeFor(currency) {
  return CURRENCY_LOCALE[resolve(currency)] || 'en-US';
}
